import React from "react";
import { Spinner, MessageBar, Button, makeStyles, tokens } from "@fluentui/react-components";

/**
 * Initial-load splash shown before WorkflowIntelligenceContext is ready.
 * Surfaces the context's error message with a retry action when init fails.
 */

const useStyles = makeStyles({
  container: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "100vh",
    width: "100%",
    padding: tokens.spacingVerticalXXL,
    backgroundColor: tokens.colorNeutralBackground1,
    overflow: "hidden",
    boxSizing: "border-box",
  },
  content: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: tokens.spacingVerticalXL,
    maxWidth: "400px",
    textAlign: "center",
  },
  messageBar: {
    width: "100%",
    padding: tokens.spacingHorizontalM,
  },
  retryButton: {
    marginTop: tokens.spacingVerticalM,
  },
  errorContainer: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalS,
  },
});

interface LoadingPageProps {
  isLoading: boolean;
  error: string | null;
  onRetry: () => void;
}

const LoadingPage: React.FC<LoadingPageProps> = ({ isLoading, error, onRetry }) => {
  const styles = useStyles();

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        {isLoading && !error && (
          <>
            <Spinner size="extra-large" label="Initializing add-in..." />
          </>
        )}

        {error && (
          <>
            <div className={styles.errorContainer}>
              <strong>Initialization Failed</strong>
              <MessageBar
                intent="error"
                className={styles.messageBar}
              >
                {error}
              </MessageBar>
            </div>
            <Button
              appearance="primary"
              onClick={onRetry}
              className={styles.retryButton}
            >
              Retry
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

export default LoadingPage;
